import type { InkStroke } from './types';

/**
 * Die Handschriftseite als SVG — und zurück.
 *
 * Die Datei muss zweierlei können: von jedem Betrachter darstellbar sein (sie landet in der
 * Ablage und geht später an den Server) und von der App **weiter bearbeitbar** sein. Deshalb
 * trägt sie beides: gezeichnete Pfade für die Anzeige und die Striche als JSON in einem
 * `<metadata>`-Element für den Rückweg.
 *
 * Die Koordinaten bleiben auf 0…1 normiert, wie sie im Zustand liegen — deshalb
 * `viewBox="0 0 1 1"` und eine Strichstärke in derselben Einheit.
 */

export const INK_METADATA_ID = 'osce-ink';

/** Strichstärke in Einheiten der viewBox; entspricht der Anzeige im Bogen. */
const BREITE = 0.004;

const esc = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const unesc = (text: string) =>
  text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

const zahl = (v: number) => Number(v.toFixed(4)).toString();

function pfad(stroke: InkStroke): string {
  if (stroke.points.length === 1) {
    const p = stroke.points[0];
    // Ein einzelner Punkt hat keinen Pfad — als Kreis bliebe er sonst unsichtbar.
    return '<circle cx="' + zahl(p.x) + '" cy="' + zahl(p.y) + '" r="' + zahl(BREITE) + '" />';
  }
  const d = stroke.points
    .map((p, i) => (i === 0 ? 'M' : 'L') + zahl(p.x) + ' ' + zahl(p.y))
    .join(' ');
  return '<path d="' + d + '" fill="none" stroke="currentColor" stroke-width="' + zahl(BREITE) +
    '" stroke-linecap="round" stroke-linejoin="round" />';
}

export function strokesToSvg(strokes: InkStroke[]): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1">' +
    '<metadata id="' + INK_METADATA_ID + '">' + esc(JSON.stringify(strokes)) + '</metadata>' +
    strokes.map(pfad).join('') +
    '</svg>'
  );
}

export function svgToStrokes(svg: string): InkStroke[] {
  const treffer = svg.match(
    new RegExp('<metadata id="' + INK_METADATA_ID + '">([\\s\\S]*?)</metadata>'),
  );
  if (!treffer) return [];
  try {
    const geparst: unknown = JSON.parse(unesc(treffer[1]));
    // Gültiges JSON, aber keine Liste (etwa eine künftige, anders aufgebaute Fassung) wäre sonst
    // ungeprüft durchgereicht und ließe jeden Aufrufer, der `.length` oder `.map()` erwartet,
    // abstürzen — eine leere Seite ist auch hier das kleinere Übel.
    return Array.isArray(geparst) ? (geparst as InkStroke[]) : [];
  } catch {
    // Beschädigt oder fremd — eine leere Seite ist besser als ein Absturz beim Öffnen.
    return [];
  }
}
